import type { FleetV2Response } from '@emma/api_client/types';
import { Box, Button, Flex, Link, Tooltip } from '@lego/klik-ui';
import { useState } from 'react';
import { Link as ReactRouterLink, useNavigate } from 'react-router-dom';
import { ConfirmationDialog } from '@/components/common/ConfirmationDialog';
import type { DetailSectionProps } from '@/components/common/details/DetailsBox';
import { UserNameLink } from '@/components/common/UserNameLink';
import { EditableDeploymentStrategyMenu } from '@/components/fleet/EditableDeploymentStrategyMenu';
import { EditableFleetDetails } from '@/components/fleet/EditableFleetDetails';
import { useDeleteFleet } from '@/hooks/fleet';
import { useCanWriteById } from '@/hooks/workspace';
import { dateTimeFormat } from '@/utility/date';
import { deploymentStrategyDocURL } from '@/utility/external_urls';
import { paths } from '@/utility/global_paths';
import { WorkspaceTextLink } from '../workspace/WorkspaceTextLink';

type Props = {
  fleet: FleetV2Response;
};

const buildFleetSections = (
  fleet: FleetV2Response,
  beatsDashboardUrl: string,
  otelDashboardUrl: string,
  canWrite: boolean
) =>
  [
    {
      title: 'General',
      items: {
        ID: { body: fleet.id },
        Description: { body: fleet.description || '-' },
        'Workspace ID': {
          body: <WorkspaceTextLink workspaceId={fleet.workspaceId} />,
        },
        'Created By': { body: <UserNameLink oid={fleet.createdByOid} /> },
        Created: { body: new Date(fleet.createdAt).toLocaleString(dateTimeFormat.local) },
      },
    },
    {
      title: 'Deployment',
      items: {
        'Active Deployment': {
          body: fleet.activeDeploymentId ? (
            <Link
              as={ReactRouterLink as never}
              to={paths.deploymentById(fleet.workspaceId, fleet.id, fleet.activeDeploymentId)}
            >
              {fleet.activeDeploymentId}
            </Link>
          ) : (
            '-'
          ),
        },
        Observability: {
          body: (
            <Flex align="center" direction="row" gap="2">
              <Tooltip label="Devices running core v14 and below">
                <Link href={beatsDashboardUrl} isExternal>
                  Beats
                </Link>
              </Tooltip>
              <Box as="span" color="gray.400">
                |
              </Box>
              <Tooltip label="Devices running core v15 and above">
                <Link href={otelDashboardUrl} isExternal>
                  OTel
                </Link>
              </Tooltip>
            </Flex>
          ),
        },
        ...(canWrite
          ? {
              'Deployment Strategy': {
                documentationLink: deploymentStrategyDocURL,
                body: <EditableDeploymentStrategyMenu fleet={fleet} />,
              },
            }
          : {}),
      },
    },
  ] as DetailSectionProps[];

export const FleetDetails = ({ fleet }: Props) => {
  const canWrite = useCanWriteById(fleet.workspaceId);
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const navigate = useNavigate();
  const deleteFleet = useDeleteFleet(fleet.workspaceId, fleet.id);

  const fleetBeatsDashboardURL = `${import.meta.env.VITE_ELASTIC_URL}/app/dashboards#/view/656dfd9a-7bf7-458a-ad4c-301691c8953a?_g=(filters:!(('$state':(store:globalState),meta:(key:metadata.fleets,params:(query:${fleet.id}),type:phrase),query:(match_phrase:(metadata.fleets:${fleet.id})))),refreshInterval:(pause:!t,value:60000),time:(from:now-15m,to:now))`;
  const fleetOTelDashboardURL = `${import.meta.env.VITE_ELASTIC_URL}/app/dashboards#/view/c3d4e5f6-a7b8-9012-cdef-123456789012?_g=(filters:!(('$state':(store:globalState),meta:(key:metadata.fleets,params:(query:${fleet.id}),type:phrase),query:(match_phrase:(metadata.fleets:${fleet.id})))),refreshInterval:(pause:!t,value:60000),time:(from:now-15m,to:now))`;

  const sections = buildFleetSections(
    fleet,
    fleetBeatsDashboardURL,
    fleetOTelDashboardURL,
    canWrite
  );

  const handleDelete = async () => {
    setIsDeleting(true);
    try {
      await deleteFleet();
      navigate(paths.workspaceFleets(fleet.workspaceId));
      setIsDeleteDialogOpen(false);
    } catch {
      // Already toasted and logged by the mutation hook; caught only to
      // avoid an unhandled promise rejection here.
    } finally {
      setIsDeleting(false);
    }
  };

  const deleteButton = canWrite ? (
    <Button
      colorScheme="error"
      isDisabled={isDeleting}
      isLoading={isDeleting}
      onClick={() => setIsDeleteDialogOpen(true)}
      size="sm"
      variant="outline"
    >
      Delete Fleet
    </Button>
  ) : undefined;

  return (
    <>
      <EditableFleetDetails additionalComponent={deleteButton} fleet={fleet} sections={sections} />
      <ConfirmationDialog
        body="Are you sure you want to delete this fleet? This action cannot be undone. Only empty fleets (no devices or deployments) can be deleted."
        header="Delete Fleet"
        isOpen={isDeleteDialogOpen}
        onClose={() => setIsDeleteDialogOpen(false)}
        onConfirmed={handleDelete}
      />
    </>
  );
};
